import { useState, useEffect } from 'react';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import PatientNav from './PatientNav';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { FaPlus, FaSearch, FaClipboardList, FaCalendar, FaStethoscope, FaTimes, FaCheck, FaClock } from 'react-icons/fa';
import Loader from '../components/Loader';

const TreatmentPlan = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showTreatmentDetails, setShowTreatmentDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [treatmentPlans, setTreatmentPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { nationalId } = useParams();

  const [newTreatmentPlan, setNewTreatmentPlan] = useState({
    date: '',
    diagnosis: '',
    treatmentGoals: '',
    procedures: '',
    timeline: '',
    estimatedCost: '',
    doctor: user?.id || '',
    patient: nationalId,
    notes: '',
    status: 'active',
    priority: 'medium'
  });

  useEffect(() => {
    const fetchTreatmentPlans = async () => {
      try {
        console.log('Fetching treatment plans for nationalId:', nationalId);
        // This endpoint needs to be created in the backend
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/treatment-plans/patient/${nationalId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        console.log('Treatment plans fetched:', response.data);
        setTreatmentPlans(response.data || []);
      } catch (err) {
        console.error('Error fetching treatment plans:', err.response?.data || err.message);
        // For now, set empty array if endpoint doesn't exist
        setTreatmentPlans([]);
      } finally {
        setLoading(false);
      }
    };
    if (token && nationalId) fetchTreatmentPlans();
  }, [nationalId, token]);

  const validateForm = () => {
    if (!newTreatmentPlan.date) return 'Date is required';
    if (!newTreatmentPlan.diagnosis) return 'Diagnosis is required';
    if (!newTreatmentPlan.treatmentGoals) return 'Treatment goals are required';
    if (!newTreatmentPlan.procedures) return 'Procedures are required';
    if (!newTreatmentPlan.timeline) return 'Timeline is required';
    return '';
  };

  const handleAddTreatmentPlan = async (e) => {
    e.preventDefault();
    if (!user?.id) {
      setError('You must be logged in as a dentist to add treatment plans.');
      return;
    }
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      console.log('Creating treatment plan:', newTreatmentPlan);
      // This endpoint needs to be created in the backend
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/treatment-plans`, newTreatmentPlan, {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Treatment plan created:', response.data);
      setTreatmentPlans([response.data, ...treatmentPlans]);
      setNewTreatmentPlan({
        date: '',
        diagnosis: '',
        treatmentGoals: '',
        procedures: '',
        timeline: '',
        estimatedCost: '',
        doctor: user.id,
        patient: nationalId,
        notes: '',
        status: 'active',
        priority: 'medium'
      });
      setShowAddForm(false);
      setError('');
    } catch (err) {
      console.error('Error creating treatment plan:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to add treatment plan');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTreatmentPlan({ ...newTreatmentPlan, [name]: value });
  };

  const filteredTreatmentPlans = treatmentPlans.filter((plan) =>
    plan.diagnosis.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.treatmentGoals.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plan.procedures.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateValue) => {
    try {
      let date;
      if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (dateValue && dateValue.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else if (dateValue && dateValue._seconds) {
        date = new Date(dateValue._seconds * 1000);
      } else {
        return 'Date not available';
      }
      
      if (isNaN(date.getTime())) {
        return 'Date not available';
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Date not available';
    }
  };

  const deleteTreatmentPlan = async (id) => {
    try {
      console.log('Deleting treatment plan:', id);
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/treatment-plans/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTreatmentPlans(treatmentPlans.filter((plan) => plan._id !== id));
      setShowTreatmentDetails(null);
    } catch (err) {
      console.error('Error deleting treatment plan:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Failed to delete treatment plan');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'on-hold': return 'bg-yellow-100 text-yellow-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0 },
  };

  if (!user || user.role !== 'dentist') {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center max-w-md bg-white p-8 rounded-xl shadow-sm border border-gray-100"
              >
                <div className="text-[#0077B6] mb-4">
                  <FaStethoscope className="h-12 w-12 mx-auto" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Access Denied</h3>
                <p className="text-gray-600 mb-6">Please log in as a dentist to view this page.</p>
              </motion.div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  if (loading) {
    return <Loader />;
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <PatientNav />
        <main className="flex-1 overflow-y-auto p-4 md:p-6 bg-gradient-to-br from-[#0077B6]/5 to-white">
          <div className="max-w-7xl mx-auto">
            {error && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="mb-6 p-4 bg-red-100 text-[#333333] rounded-lg"
              >
                {error}
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                  <h1 className="text-3xl md:text-4xl font-bold text-[#0077B6] mb-1">
                    Treatment Plans
                  </h1>
                  <p className="text-gray-600">Create and manage patient treatment plans</p>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setShowAddForm(true)}
                  className="w-full md:w-auto bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white px-6 py-2.5 rounded-full font-medium hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center"
                >
                  <FaPlus className="h-5 w-5 mr-2" />
                  New Treatment Plan
                </motion.button>
              </div>
              <motion.div
                whileHover={{ scale: 1.02 }}
                className="relative mb-6"
              >
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder="Search treatment plans..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-full bg-white shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0077B6] focus:bg-white sm:text-sm transition-all duration-200"
                />
              </motion.div>
              <motion.div
                variants={container}
                initial="hidden"
                animate="show"
                className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
              >
                {filteredTreatmentPlans.length === 0 ? (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="p-8 text-center"
                  >
                    <div className="mx-auto h-16 w-16 text-gray-400 mb-4">
                      <FaClipboardList className="h-16 w-16" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-2">No treatment plans found</h3>
                    <p className="text-gray-500">
                      {searchTerm ? 'Try a different search term' : 'Get started by creating a new treatment plan'}
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setShowAddForm(true)}
                      className="mt-6 inline-flex items-center px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium shadow-md hover:shadow-lg"
                    >
                      <FaPlus className="h-5 w-5 mr-2" />
                      Create Treatment Plan
                    </motion.button>
                  </motion.div>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {filteredTreatmentPlans.map((plan) => (
                      <motion.li
                        key={plan._id}
                        variants={item}
                        className="hover:bg-gray-50 transition-colors cursor-pointer"
                        onClick={() => setShowTreatmentDetails(plan)}
                      >
                        <div className="px-6 py-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="h-3 w-3 rounded-full bg-[#FFD700]"></div>
                              <h3 className="text-lg font-medium text-gray-800">{plan.diagnosis}</h3>
                            </div>
                            <div className="text-sm text-gray-500">{formatDate(plan.date)}</div>
                          </div>
                          <div className="mt-2 flex justify-between items-center">
                            <p className="text-sm text-gray-600">
                              <span className="font-medium">Timeline:</span> {plan.timeline}
                            </p>
                            <div className="flex gap-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(plan.status)}`}>
                                {plan.status}
                              </span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(plan.priority)}`}>
                                {plan.priority}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.li>
                    ))}
                  </ul>
                )}
              </motion.div>
            </motion.div>
            {showAddForm && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">New Treatment Plan</h2>
                    <button
                      onClick={() => setShowAddForm(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <form onSubmit={handleAddTreatmentPlan} className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Date*</label>
                        <div className="relative">
                          <FaCalendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="date"
                            name="date"
                            value={newTreatmentPlan.date}
                            onChange={handleInputChange}
                            className="pl-10 w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                            required
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Priority</label>
                        <select
                          name="priority"
                          value={newTreatmentPlan.priority}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        >
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Diagnosis*</label>
                      <textarea
                        name="diagnosis"
                        value={newTreatmentPlan.diagnosis}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Treatment Goals*</label>
                      <textarea
                        name="treatmentGoals"
                        value={newTreatmentPlan.treatmentGoals}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Procedures*</label>
                      <textarea
                        name="procedures"
                        value={newTreatmentPlan.procedures}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                        required
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Timeline*</label>
                        <input
                          type="text"
                          name="timeline"
                          value={newTreatmentPlan.timeline}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          placeholder="e.g., 6-8 weeks"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1.5">Estimated Cost</label>
                        <input
                          type="text"
                          name="estimatedCost"
                          value={newTreatmentPlan.estimatedCost}
                          onChange={handleInputChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                          placeholder="e.g., $500"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">Notes (Optional)</label>
                      <textarea
                        name="notes"
                        value={newTreatmentPlan.notes}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0077B6] focus:border-[#0077B6]"
                        rows="3"
                      />
                    </div>
                    <div className="flex justify-end space-x-4">
                      <motion.button
                        type="button"
                        onClick={() => setShowAddForm(false)}
                        className="px-6 py-2 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 font-medium transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Cancel
                      </motion.button>
                      <motion.button
                        type="submit"
                        className="px-6 py-2 bg-gradient-to-r from-[#0077B6] to-[#20B2AA] text-white rounded-full hover:from-[#0077B6]/90 hover:to-[#20B2AA]/90 font-medium transition-colors shadow-md hover:shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Save
                      </motion.button>
                    </div>
                  </form>
                </motion.div>
              </div>
            )}
            {showTreatmentDetails && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl border border-gray-100"
                >
                  <div className="p-6 border-b flex justify-between items-center bg-[#0077B6]/10">
                    <h2 className="text-xl font-semibold text-[#0077B6]">Treatment Plan Details</h2>
                    <button
                      onClick={() => setShowTreatmentDetails(null)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <FaTimes className="h-6 w-6" />
                    </button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Date</p>
                        <p className="text-sm text-gray-900">{formatDate(showTreatmentDetails.date)}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Priority</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(showTreatmentDetails.priority)}`}>
                          {showTreatmentDetails.priority}
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Diagnosis</p>
                      <p className="text-sm text-gray-900">{showTreatmentDetails.diagnosis}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Treatment Goals</p>
                      <p className="text-sm text-gray-900">{showTreatmentDetails.treatmentGoals}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Procedures</p>
                      <p className="text-sm text-gray-900">{showTreatmentDetails.procedures}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Timeline</p>
                        <p className="text-sm text-gray-900">{showTreatmentDetails.timeline}</p>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-500">Estimated Cost</p>
                        <p className="text-sm text-gray-900">{showTreatmentDetails.estimatedCost || 'Not specified'}</p>
                      </div>
                    </div>
                    {showTreatmentDetails.notes && (
                      <div>
                        <p className="text-sm font-medium text-gray-500">Notes</p>
                        <p className="text-sm text-gray-900">{showTreatmentDetails.notes}</p>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-500">Status</p>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(showTreatmentDetails.status)}`}>
                          {showTreatmentDetails.status}
                        </span>
                      </div>
                    </div>
                    <div className="flex justify-end gap-3">
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => deleteTreatmentPlan(showTreatmentDetails._id)}
                        className="px-6 py-2 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-full hover:from-red-600 hover:to-red-700 font-medium transition-colors shadow-md"
                      >
                        Delete
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TreatmentPlan;

